// Design tokens extracted from Figma - Telco Edge design

export const figmaTokens = {
  colors: {
    // Background colors from Figma
    background: {
      primary: '#FFFFFF',      // Main background
      secondary: '#F0F4F5',    // Secondary background from Frame 117
      tertiary: '#EFF4FC',     // Tertiary background from topbar search
      card: '#FFFFFF',         // Card backgrounds
      overlay: 'rgba(0, 0, 0, 0.5)',
      elevated: '#FFFFFF',     // Elevated surfaces
    },
    
    // Text colors from Figma
    text: {
      primary: '#232323',      // Primary text color
      secondary: '#121212',    // Secondary text (darker)
      muted: '#AAAAAA',        // Muted text from search placeholder
      placeholder: '#BBBBBB',  // Inactive icon color
      inverse: '#FFFFFF',      // White text
    },
    
    // Border colors from Figma
    border: {
      primary: '#E0E0E0',                    // Primary border color
      secondary: '#D6D6D6',                  // Secondary border
      muted: 'rgba(216, 216, 216, 0.5)',    // Muted border from search
      focus: '#3B82F6',                      // Focus border
    },
    
    // Brand colors
    brand: {
      primary: '#3B82F6',      // Primary brand color (blue)
      secondary: '#2563EB',    // Secondary brand (darker blue)
      accent: '#1D4ED8',       // Accent color
    },
    
    // Status colors for order types and states
    status: {
      pending: {
        background: '#FEF3C7',  // Light yellow background
        text: '#D97706',        // Dark yellow text
        border: '#F59E0B',      // Yellow border
      },
      approved: {
        background: '#D1FAE5',  // Light green background
        text: '#059669',        // Dark green text
        border: '#10B981',      // Green border
      },
      completed: {
        background: '#D1FAE5',  // Light green background
        text: '#059669',        // Dark green text
        border: '#10B981',      // Green border
      },
      rejected: {
        background: '#FEE2E2',  // Light red background
        text: '#DC2626',        // Dark red text
        border: '#EF4444',      // Red border
      },
    },
    
    // Interactive states
    interactive: {
      hover: '#F8FAFC',        // Hover background
      active: '#F1F5F9',       // Active background
      disabled: '#F3F4F6',     // Disabled background
    },
  },
  
  // Typography from Figma
  typography: {
    fontFamily: {
      primary: ['Lexend', 'system-ui', '-apple-system', 'sans-serif'],
      mono: ['SF Mono', 'Monaco', 'Cascadia Code', 'monospace'],
    },
    fontSize: {
      xs: '12px',              // Small text (⌘K shortcut)
      sm: '14px',              // Secondary text
      base: '16px',            // Primary text size
      lg: '18px',              // Large text
      xl: '20px',              // Extra large
      '2xl': '24px',           // Headers
    },
    fontWeight: {
      light: 300,              // Lexend light
      normal: 400,             // Lexend regular
      medium: 500,             // Medium weight
      semibold: 600,           // Semibold
      bold: 700,               // Bold
    },
    lineHeight: {
      tight: 1.25,             // Tight line height
      normal: 1.5,             // Normal line height
      relaxed: 1.625,          // Relaxed line height
    },
    letterSpacing: {
      tight: '-0.025em',
      normal: '0em',
      wide: '0.025em',
      wider: '0.05em',
    },
  },
  
  // Spacing values from Figma measurements
  spacing: {
    px: '1px',
    0.5: '2px',
    1: '4px',
    1.5: '6px',              // 6px gaps in Figma
    2: '8px',
    2.5: '10px',             // 10px gaps
    3: '12px',
    3.5: '14px',
    4: '16px',
    5: '20px',               // 20px gaps in navigation
    6: '24px',
    7: '28px',
    8: '32px',
    9: '36px',
    10: '40px',              // 40px gaps between sections
    12: '48px',
    16: '64px',
    20: '80px',
  },
  
  // Border radius values from Figma
  borderRadius: {
    none: '0px',
    xs: '4px',               // Checkbox border radius
    sm: '7px',               // Small components
    base: '8px',             // Base border radius
    md: '10px',              // Medium components
    lg: '12px',              // Cards and containers
    xl: '15px',              // Topbar elements
    '2xl': '17px',           // Larger components
    '3xl': '20px',           // Large containers
    '4xl': '25px',           // Very large containers
    pill: '58px',            // Pill-shaped badges
    full: '9999px',          // Circular elements
  },
  
  // Shadow system
  shadows: {
    none: 'none',
    subtle: '0px 28.8px 48px 0px rgba(0, 0, 0, 0.09)',  // From profile dropdown
    card: '0 1px 3px 0 rgb(0 0 0 / 0.1)',              // Card shadows
    dropdown: '0 4px 6px -1px rgb(0 0 0 / 0.1)',       // Dropdown shadows
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',           // Large shadows
  },
  
  // Animation and transitions
  animation: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    easing: {
      ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  
  // Layout dimensions from Figma
  layout: {
    // Topbar
    topbar: {
      height: '64px',         // Topbar height from Figma
      padding: '15px',        // Topbar padding
    },
    
    // Sidebar navigation
    sidebar: {
      width: '240px',         // Standard sidebar width
      collapsedWidth: '55px', // Icon-only width
      padding: '20px',        // Sidebar padding
      itemHeight: '55px',     // Navigation item height
    },
    
    // Content areas
    container: {
      padding: '10px',        // Container padding
      gap: '10px',            // Gap between sections
    },
    
    // Component dimensions
    button: {
      height: {
        sm: '32px',
        md: '40px',
        lg: '48px',
      },
      padding: {
        sm: '8px 12px',
        md: '13px 20px',       // From topbar buttons
        lg: '16px 24px',
      },
    },
    
    // Form elements
    input: {
      height: '56px',         // Search input height
      padding: '13px 20px',   // Search input padding
    },
    
    // Cards and containers
    card: {
      padding: '20px',        // Standard card padding
      gap: '12px',           // Gap between card elements
    },
  },
  
  // Z-index scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
} as const;

// Dark theme variations
export const darkThemeTokens = {
  colors: {
    background: {
      primary: '#1a1a1a',
      secondary: '#262626',
      tertiary: '#333333',
      card: '#2a2a2a',
      overlay: 'rgba(0, 0, 0, 0.8)',
      elevated: '#333333',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e5e5e5',
      muted: '#a3a3a3',
      placeholder: '#737373',
      inverse: '#000000',
    },
    border: {
      primary: '#404040',
      secondary: '#525252',
      muted: 'rgba(115, 115, 115, 0.5)',
      focus: '#60a5fa',
    },
    brand: {
      primary: '#60a5fa',      // Lighter blue for dark mode
      secondary: '#3b82f6',
      accent: '#2563eb',
    },
    interactive: {
      hover: '#404040',
      active: '#525252',
      disabled: '#262626',
    },
  },
} as const;

// Type definitions for better TypeScript support
export type FigmaTokens = typeof figmaTokens;
export type ColorToken = keyof typeof figmaTokens.colors;
export type SpacingToken = keyof typeof figmaTokens.spacing;
export type TypographyToken = keyof typeof figmaTokens.typography;
export type BorderRadiusToken = keyof typeof figmaTokens.borderRadius;

// Helper function to get nested token values
export function getTokenValue(tokens: any, path: string): string {
  return path.split('.').reduce((obj, key) => obj?.[key], tokens) || '';
}

// Generate CSS custom properties from tokens
export function generateCssVariables(tokens: any, prefix = '', result: Record<string, string> = {}): Record<string, string> {
  Object.entries(tokens).forEach(([key, value]) => {
    const cssVar = prefix ? `${prefix}-${key}` : key;
    
    if (typeof value === 'object' && value !== null) {
      generateCssVariables(value, cssVar, result);
    } else {
      result[cssVar] = String(value);
    }
  });
  
  return result;
}

export default figmaTokens;
